// Copyright (c) Docugami, Inc. All rights reserved.

import { Config } from 'config';
import { v4 as uuidv4 } from 'uuid';
import { ClientCorrelationIds } from './ClientCorrelationIds';

export interface ITelemetryContext {
    source: string; // Source of the event. The client always specifies 'client'
    environment: string; // The current environment - dev, staging, prod
    organizationId: string; // ID of the active organization
    subscriptionType: string; // Friendly name of the subscription
    devAlias: string; // Dev alias specified in config.ts
    userId: string; // The e-mail address of the active user
    documentSetId: string; // The document set ID of the current context
    documentId: string; // The document ID of the current context
    chunkInstanceId: string; // The chunk instance ID of the current context
    explorerDocumentSetId: string; // The document set ID within the Doc Explorer
    explorerDocumentId: string; // The document ID within the Doc Explorer
    explorerTab: string; // The active tab of the Doc Explorer
    explorerNavigationGroup: string // The active navigation group
    explorerNavigationId: string // The active navigation ID in the doc explorer
    workPathId: string; // The ID of the active work path
    workPathType: string; // The type of the active work path
    reportId: string; // The report ID of the current context if applicable
    clientInstanceId: string; // A unique ID for the client instance. Stored in memory. Persists only as long as the active browser page. Refreshing the page will reset this value.
    clientLocalSessionId: string; // A unique ID for the local client session. Stored in session storage. Persists only as long as the active browser tab/session.
    clientGlobalSessionId: string; // A unique ID for the global client session. Stored in local storage. Persists for a given browser on a given device until the user clears the cache.
    verboseChat: boolean; // Whether the current chat session has the verbose flag on or off
}

interface ContextItem {
    id: string;
    context: Partial<ITelemetryContext>;
}

class TelemetryContextImpl {
    private readonly _clientCorrelationIds: ClientCorrelationIds = new ClientCorrelationIds();
    private _contextItems: ContextItem[] = [];
    private _cache?: Partial<ITelemetryContext>;

    get context(): Partial<ITelemetryContext> {
        if (!this._cache) {
            this._cache = this.createContext();
        }

        return this._cache;
    }

    add(context: Partial<ITelemetryContext>): () => void {
        this._cache = undefined;

        const id = uuidv4();
        this._contextItems.push({
            id: id,
            context: { ...context }
        });

        return () => this.remove(id);
    }

    private remove(id: string) {
        this._cache = undefined;

        const index = this._contextItems.findIndex(x => x.id === id);
        if (index !== -1) {
            this._contextItems.splice(index, 1);
        }
    }

    private createContext() {
        const initial: Partial<ITelemetryContext> = {
            source: "client",
            environment: process.env.REACT_APP_ENV,
            clientInstanceId: this._clientCorrelationIds.instanceId,
            clientLocalSessionId: this._clientCorrelationIds.localSessionId,
            clientGlobalSessionId: this._clientCorrelationIds.globalSessionId
        };

        if (!!Config.devAlias) {
            initial.devAlias = Config.devAlias;
        }

        return this._contextItems.reduce<Partial<ITelemetryContext>>(
            (prev, cur) => {
                return {
                    ...prev,
                    ...cur.context
                };
            },
            initial);
    }
}

export const TelemetryContext = new TelemetryContextImpl();
